import React, { useState, useEffect, useMemo } from 'react';
import { API_ENDPOINTS, apiUtils } from '../config/api';
import PremiumAssignmentResults from './PremiumAssignmentResults';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  Pagination,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Search,
  FilterList,
  Person,
  Email,
  CalendarToday,
  Assessment as AssessmentIcon,
  Business,
  PlayArrow,
  AutoFixHigh,
  BarChart
} from '@mui/icons-material';

interface Assignment {
  id: string;
  name: string;
  email_id: string;
  token: string;
  assessment_id: string;
  tenant_access_id: string;
  overall_score: number | null;
  is_active: boolean;
  attempted_date: string | null;
  completed_date: string | null;
  created_at: string;
}

interface AssessmentType {
  id: string;
  name: string;
  language_id: number;
  configuration: any;
}

interface TenantAccess {
  id: string;
  tenant_id: number;
  is_active: boolean;
}

const TalentAssignmentManager: React.FC = () => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [assessments, setAssessments] = useState<AssessmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [questionsSummary, setQuestionsSummary] = useState<any>(null);
  const [assignmentResults, setAssignmentResults] = useState<any>(null);
  
  // Search, Filter, Sort, Pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all assignments using API utils
      const assignmentData = await apiUtils.get(API_ENDPOINTS.ASSIGNMENTS);
      setAssignments(assignmentData.data || []);

      // Fetch assessments
      const assessmentData = await apiUtils.get(API_ENDPOINTS.ASSESSMENTS);
      setAssessments(assessmentData.data || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAssessmentName = useMemo(() => (assessmentId: string) => {
    const assessment = assessments.find(a => a.id === assessmentId);
    return assessment ? assessment.name : 'Unknown Assessment';
  }, [assessments]);

  const formatDate = (dateString: string | null) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'Not started';
  };

  const handleStartAssignment = async (assignment: Assignment) => {
    try {
      // First, start the assignment and generate questions
      const startResponse = await apiUtils.post(API_ENDPOINTS.START_ASSIGNMENT_QUESTIONS(assignment.id), {});
      
      if (startResponse.success) {
        console.log('Assignment started successfully:', startResponse.data);
        
        // Navigate directly to exam instructions
        window.open(`/exam/${assignment.id}/instructions`, '_blank');
        
        // Refresh data to show updated status
        fetchData();
      } else {
        alert('Error starting assignment: ' + startResponse.message);
      }
    } catch (error) {
      console.error('Error starting assignment:', error);
      alert('Error starting assignment. Please try again.');
    }
  };

  const handleViewQuestions = async (assignment: Assignment) => {
    try {
      const summaryUrl = `${API_ENDPOINTS.GET_ASSIGNMENT_QUESTIONS(assignment.id)}/summary`;
      const result = await apiUtils.get(summaryUrl);
      
      if (result.success) {
        setSelectedAssignment(assignment);
        setQuestionsSummary(result.data);
      } else {
        alert('Error fetching questions: ' + result.message);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      alert('Error fetching questions');
    }
  };

  const handleGenerateQuestions = async (assignment: Assignment) => {
    try {
      const result = await apiUtils.post(API_ENDPOINTS.GENERATE_ASSIGNMENT_QUESTIONS(assignment.id), {});
      
      if (result.success) {
        alert(`Questions generated successfully!\nTotal: ${result.data.total_questions} questions`);
        fetchData();
      } else {
        alert('Error generating questions: ' + result.message);
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Error generating questions');
    }
  };

  const handleViewResults = async (assignment: Assignment) => {
    try {
      // Use the new simplified scoring API endpoint
      const resultsUrl = `${API_ENDPOINTS.GET_ASSIGNMENT_RESULTS_SIMPLE(assignment.id)}`;
      const result = await apiUtils.get(resultsUrl);
      
      console.log('TalentAssignmentManager - Simplified API Result:', result);
      
      // Check for our API response structure: { message, data, status }
      if (result.status === 'success' && result.data) {
        setSelectedAssignment(assignment);
        setAssignmentResults(result.data);
      } else {
        console.error('Invalid API response structure:', result);
        alert('Error fetching results: ' + (result.message || 'Invalid response format'));
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      alert('Error fetching results: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Filtered, sorted, and paginated assignments
  const filteredAndSortedAssignments = useMemo(() => {
    let filtered = assignments.filter(assignment => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        assignment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.email_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getAssessmentName(assignment.assessment_id).toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'completed' && assignment.completed_date) ||
        (statusFilter === 'in-progress' && assignment.attempted_date && !assignment.completed_date) ||
        (statusFilter === 'not-started' && !assignment.attempted_date) ||
        (statusFilter === 'active' && assignment.is_active) ||
        (statusFilter === 'inactive' && !assignment.is_active);
      
      return matchesSearch && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'email_id':
          aValue = a.email_id.toLowerCase();
          bValue = b.email_id.toLowerCase();
          break;
        case 'assessment':
          aValue = getAssessmentName(a.assessment_id).toLowerCase();
          bValue = getAssessmentName(b.assessment_id).toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'overall_score':
          aValue = a.overall_score || 0;
          bValue = b.overall_score || 0;
          break;
        default:
          aValue = a.created_at;
          bValue = b.created_at;
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [assignments, searchTerm, statusFilter, sortBy, sortOrder, getAssessmentName]);

  // Paginated assignments
  const paginatedAssignments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedAssignments.slice(startIndex, endIndex);
  }, [filteredAndSortedAssignments, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedAssignments.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading talent assignments...</Typography>
      </Box>
    );
  }

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Card sx={{ 
        borderRadius: '16px', 
        mb: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            🎯 Talent Assignment Manager
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Manage talent assignments and track progress
          </Typography>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
        <Card sx={{ borderRadius: '12px', textAlign: 'center', flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Avatar sx={{ bgcolor: '#667eea', mx: 'auto', mb: 2 }}>
              <AssessmentIcon />
            </Avatar>
            <Typography variant="h4" fontWeight={700} color="#667eea">
              {assignments.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Assignments
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '12px', textAlign: 'center', flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Avatar sx={{ bgcolor: '#10b981', mx: 'auto', mb: 2 }}>
              <BarChart />
            </Avatar>
            <Typography variant="h4" fontWeight={700} color="#10b981">
              {assignments.filter(a => a.completed_date).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '12px', textAlign: 'center', flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Avatar sx={{ bgcolor: '#f59e0b', mx: 'auto', mb: 2 }}>
              <CalendarToday />
            </Avatar>
            <Typography variant="h4" fontWeight={700} color="#f59e0b">
              {assignments.filter(a => a.attempted_date && !a.completed_date).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '12px', textAlign: 'center', flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Avatar sx={{ bgcolor: '#6b7280', mx: 'auto', mb: 2 }}>
              <Person />
            </Avatar>
            <Typography variant="h4" fontWeight={700} color="#6b7280">
              {assignments.filter(a => !a.attempted_date).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Not Started
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '12px', textAlign: 'center', flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Avatar sx={{ bgcolor: '#8b5cf6', mx: 'auto', mb: 2 }}>
              <FilterList />
            </Avatar>
            <Typography variant="h4" fontWeight={700} color="#8b5cf6">
              {filteredAndSortedAssignments.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filtered Results
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Controls */}
      <Card sx={{ borderRadius: '12px', mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search by name, email, or assessment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 300, flex: 1 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="not-started">Not Started</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="created_at">Date Created</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="email_id">Email</MenuItem>
                <MenuItem value="assessment">Assessment</MenuItem>
                <MenuItem value="overall_score">Score</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              onClick={resetFilters}
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
            >
              Reset
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Showing {paginatedAssignments.length} of {filteredAndSortedAssignments.length} assignments
            {searchTerm && ` (filtered by "${searchTerm}")`}
          </Typography>
        </CardContent>
      </Card>

      {/* Assignments Table */}
      <Card sx={{ borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📋 Talent Assignments
          </Typography>
          
          {filteredAndSortedAssignments.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No assignments found matching your criteria
              </Typography>
              {searchTerm || statusFilter !== 'all' ? (
                <Button 
                  onClick={resetFilters} 
                  variant="contained" 
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              ) : (
                <Typography color="text.secondary">
                  Create some assignments first!
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              {paginatedAssignments.map((assignment) => (
                <Card key={assignment.id} sx={{ mb: 2, borderRadius: '12px', '&:hover': { boxShadow: 2 } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Person />
                      </Avatar>
                      <Box flex={1}>
                        <Typography fontWeight={600} variant="h6">{assignment.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {assignment.email_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Assessment: {getAssessmentName(assignment.assessment_id)} • {formatDate(assignment.created_at)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box display="flex" gap={1}>
                          <Tooltip title={assignment.attempted_date ? 'Resume Assignment' : 'Start Assignment'}>
                            <Button
                              onClick={() => handleStartAssignment(assignment)}
                              variant="contained"
                              size="small"
                              disabled={!!assignment.completed_date}
                              startIcon={<PlayArrow />}
                              sx={{ minWidth: 100 }}
                            >
                              {assignment.attempted_date ? 'Resume' : 'Start'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="View Questions">
                            <IconButton
                              onClick={() => handleViewQuestions(assignment)}
                              size="small"
                              color="primary"
                            >
                              <Email />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate Questions">
                            <IconButton
                              onClick={() => handleGenerateQuestions(assignment)}
                              size="small"
                              color="success"
                            >
                              <AutoFixHigh />
                            </IconButton>
                          </Tooltip>
                          {assignment.completed_date && (
                            <Tooltip title="View Results">
                              <IconButton
                                onClick={() => handleViewResults(assignment)}
                                size="small"
                                color="primary"
                              >
                                <BarChart />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              
              {/* Material-UI Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => handlePageChange(page)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Questions Summary Modal */}
      {selectedAssignment && questionsSummary && (
        <div className="modal-overlay" onClick={() => setSelectedAssignment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📝 Questions Summary</h2>
              <button 
                className="close-button"
                onClick={() => setSelectedAssignment(null)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="assignment-info">
                <h3>{selectedAssignment.name}</h3>
                <p><strong>Email:</strong> {selectedAssignment.email_id}</p>
                <p><strong>Assessment:</strong> {getAssessmentName(selectedAssignment.assessment_id)}</p>
              </div>

              <div className="questions-stats">
                <div className="stat-row">
                  <span>Total Questions:</span>
                  <span>{questionsSummary.total_questions}</span>
                </div>
                <div className="stat-row">
                  <span>Answered:</span>
                  <span>{questionsSummary.answered_questions}</span>
                </div>
                <div className="stat-row">
                  <span>Completion:</span>
                  <span>{questionsSummary.completion_percentage}%</span>
                </div>
                <div className="stat-row">
                  <span>Graded:</span>
                  <span>{questionsSummary.graded_questions}</span>
                </div>
              </div>

              <div className="question-types">
                <h4>Question Types:</h4>
                {Object.entries(questionsSummary.question_types).map(([type, stats]: [string, any]) => (
                  <div key={type} className="type-row">
                    <span className="type-name">{type}</span>
                    <span className="type-stats">
                      {stats.answered}/{stats.total} answered
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-primary"
                onClick={() => handleStartAssignment(selectedAssignment)}
              >
                Start Assignment
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedAssignment(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Results Modal */}
      {selectedAssignment && (
        <PremiumAssignmentResults
          open={true}
          assignmentId={selectedAssignment.id}
          onClose={() => {
            setSelectedAssignment(null);
            setAssignmentResults(null);
          }}
        />
      )}
    </Box>
  );
};

export default TalentAssignmentManager;
